// Hook que reconstruye el ResumenSesion de una sesión completada desde
// Supabase + el schema del catálogo. Usado por SesionCompletada como
// fallback cuando el route state no está (refresh, deep-link).
//
// Si SesionCompletada llega con state válido (happy path post-finalizar
// sesión), NO se llama este hook — el state es la fuente fresca y evita
// network. El parámetro `enabled=false` permite saltarse el fetch.

import { useEffect, useState } from "react";
import { supabase } from "../client";
import { getExerciseSchema } from "@/lib/pose/exerciseSchemas";
import { getViolationLabel } from "@/lib/pose/getViolationLabel";
import type { ResumenSesion } from "@/app/pages/SesionCompletada";

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export type ResumenErrorKind = "not-found" | "wrong-state" | "fetch-error";

interface State {
  data: ResumenSesion | null;
  /** "not-found" cubre tanto UUID malformado como id inexistente.
   *  "wrong-state" es sesión con estado != 'completada'. "fetch-error"
   *  cubre red, SQL, y catálogo de ejercicios desconocido. */
  errorKind: ResumenErrorKind | null;
  errorMessage: string | null;
  loading: boolean;
}

/** Shape parcial del jsonb persistido por buildPoseAnalysis. Validamos
 *  solo lo que necesitamos para reconstruir topAMejorar — el resto del
 *  jsonb queda como `unknown` por simplicidad. La columna en DB es
 *  nullable; null → topAMejorar=null en el resumen. */
interface PoseAnalysisCalidadGlobal {
  violaciones_frecuentes?: Array<{
    tipo: string;
    frecuencia_pct: number;
  }>;
}
interface PoseAnalysisShape {
  calidad_global?: PoseAnalysisCalidadGlobal;
}

interface SesionRow {
  id: string;
  ejercicio_id: string;
  estado: string;
  total_reps: number;
  duration_sec: number | null;
  score_promedio: number | null;
  pose_analysis: PoseAnalysisShape | null;
  /** Nivel 1..10 si el paciente lo reportó, null si todavía no
   *  confirmó (o si la sesión es pre-MVP, sin la columna poblada). */
  dolor_reportado: number | null;
}

export function useResumenSesion(
  sessionId: string | undefined,
  enabled = true,
): State {
  const [state, setState] = useState<State>({
    data: null,
    errorKind: null,
    errorMessage: null,
    loading: enabled && !!sessionId,
  });

  useEffect(() => {
    if (!enabled) return;

    if (!sessionId || !UUID_RE.test(sessionId)) {
      setState({
        data: null,
        errorKind: "not-found",
        errorMessage: null,
        loading: false,
      });
      return;
    }

    let cancelled = false;
    setState({
      data: null,
      errorKind: null,
      errorMessage: null,
      loading: true,
    });

    (async () => {
      const { data, error } = await supabase
        .from("sesiones")
        .select(
          "id, ejercicio_id, estado, total_reps, duration_sec, score_promedio, pose_analysis, dolor_reportado",
        )
        .eq("id", sessionId)
        .maybeSingle()
        .returns<SesionRow>();

      if (cancelled) return;

      if (error) {
        console.warn("[useResumenSesion] fetch error", error);
        setState({
          data: null,
          errorKind: "fetch-error",
          errorMessage: error.message,
          loading: false,
        });
        return;
      }

      if (!data) {
        setState({
          data: null,
          errorKind: "not-found",
          errorMessage: null,
          loading: false,
        });
        return;
      }

      if (data.estado !== "completada") {
        setState({
          data: null,
          errorKind: "wrong-state",
          errorMessage: null,
          loading: false,
        });
        return;
      }

      const schema = getExerciseSchema(data.ejercicio_id);
      if (!schema) {
        setState({
          data: null,
          errorKind: "fetch-error",
          errorMessage: `Ejercicio "${data.ejercicio_id}" no soportado por este cliente`,
          loading: false,
        });
        return;
      }

      // pose_analysis puede ser null si la persistencia jsonb falló pero el
      // estado quedó en 'completada' (caso raro). En ese caso topAMejorar
      // queda null y la UI no muestra esa stat — render sigue siendo válido.
      const topV =
        data.pose_analysis?.calidad_global?.violaciones_frecuentes?.[0];
      const topAMejorar = topV
        ? {
            summaryLabel: getViolationLabel(topV.tipo, schema),
            frecuenciaPct: topV.frecuencia_pct,
          }
        : null;

      const resumen: ResumenSesion = {
        ejercicioNombre: schema.nombre,
        durationSec: data.duration_sec ?? 0,
        repsValidos: data.total_reps,
        repsObjetivo: schema.repsObjetivo * schema.seriesObjetivo,
        qualityPromedio: data.score_promedio,
        topAMejorar,
        dolorReportado: data.dolor_reportado,
      };

      setState({
        data: resumen,
        errorKind: null,
        errorMessage: null,
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [sessionId, enabled]);

  return state;
}
